// The meeting. Zoom's controls in ClikHome's clothes: audio and video with device carets, participants, chat, share,
// reactions and a raised hand, speaker or gallery view, pin, full screen, and leave — with the host's end-for-all.
import {
  ConnectionQuality, DisconnectReason, EngineEvent, LocalAudioTrack, LocalVideoTrack, Room, RoomEvent, ScreenSharePresets, Track, VideoPresets, VideoQuality,
} from "livekit-client";
import { clearToasts, closeMenu, firstName, h, initials, menuItem, modal, showMenu, toast } from "./dom.js";
import { icon } from "./icons.js";
import { clock, formatTime } from "./format.js";
import { isCanvas, product, words } from "./product.js";
import { keepChat, moderate, readChatLog, siteLink } from "./api.js";
import { ASSIST_TOPIC, assistPanel } from "./assist.js";
import { guardTracks, isSite } from "./canvas/privacy.js";
import { TOPIC as CANVAS_TOPIC } from "./canvas/sync.js";
import { acceptAssist, callExtras, chatTargets, siteCamFrom, siteVisibleTo } from "./call-extras.js";
import { paintWaitlist, watchDoor } from "./door.js";
import { deviceForm } from "./lobby.js";
import { pickShare, SHARE_ANY, SHARE_TAB_OR_WINDOW } from "./share.js";
import { FILMSTRIP_MAX, filmstrip as stripOf, galleryPage, pageSize } from "./grid.js";
import { pickBackground, rememberedProcessor, restoreBackground } from "./bgpicker.js";
import { saved as savedBackground, supported as backgroundsHere } from "./background.js";
import { fitInside, isPortrait } from "./fit.js";
import { roomOptions } from "./rtc.js";
import { attachSheet } from "./sheet.js";
import { SENSE_TOPIC, acceptSenseMessage, createSensePanel, fromStaff, toBlock } from "./sense.js";

const STRIP_H = 46; // the faces strip at the board's full height — the same 46px as `.call.board-full .stage` in studio.css

const REACTIONS = ["👏", "👍", "❤️", "😂", "😮", "🎉"];
// The Meeting Board (D187 A). Since B2 it is live for everyone over the data channel, so on ClikMeet every person
// holds the document from the moment they join (to answer a newcomer, and to show the dot when someone edits it):
// the module is fetched at join there. Canvas never fetches it.
const boardModule = () => import("./board.js");
// A phone: narrow, or wide and short — a phone on its side is 850–930 CSS px wide and under 500 tall (re-audit 28 Sep:
// it was a laptop to the code, and got the gallery's cropped cells). studio.css keys its phone rules the same way.
const PHONE_MQ = "(max-width: 760px), (max-height: 500px) and (orientation: landscape)";
const phone = () => window.matchMedia(PHONE_MQ).matches;
// The Board's sheet is for a phone held upright: studio.css draws it only under this width, and the controller must
// run exactly where the CSS does — a phone on its side keeps the side panel (re-audit 29 Sep: the controller ran on
// PHONE_MQ and squashed the static panel).
const SHEET_MQ = "(max-width: 760px)";
const sheet = () => window.matchMedia(SHEET_MQ).matches;
// From the identity the studio's pass fixes ("host-<code>"), never from attributes, which each person can edit.
const roleOf = (p) => { const r = String(p?.identity || "").split("-")[0]; return ["host", "customer", "guest", "rm"].includes(r) ? r : "guest"; };

export async function runCall(root, { code, info, pass, joined }) {
  const isHost = pass.role === "host";
  // CR-20261009-001 L3.3 / L3.4 (D555 A): live assist and the site camera in Canvas's plain call, on an RM-hosted meeting
  // (call-extras.js). A NULL-stamp meeting gets none of it — today's call.
  const extras = callExtras({ meeting: info.meeting, role: pass.role, canvasProduct: isCanvas, inRoom: Boolean(info.inRoom || info.room) });
  // A ClikMeet room's door is kept by the host and by the colleagues ClikWork signed in (CR-20260926-001 F6): they see
  // who is waiting, admit and refuse, and mute or remove guests. Only the host ends the meeting.
  const keepsDoor = Boolean(info.room) && (isHost || pass.role === "rm");
  const coHost = keepsDoor && !isHost;
  // The room's settings — the picture's quality and the five minutes of reconnecting — are rtc.js's, shared with Canvas.
  const room = new Room(roomOptions(joined));

  const state = {
    layout: phone() ? "speaker" : "gallery",
    pinned: null,
    speaker: null,
    panel: null,
    unread: 0,
    chat: [],
    knocks: [], // who is waiting at a ClikMeet room's door (door.js)
    shareTrack: null,
    shareAudio: null, // ClikMeet: the shared screen's sound, published beside it
    startedAt: 0,
    ended: null,
    page: 0,               // the gallery page being looked at (larger calls, grid.js)
    lastSpoke: new Map(),  // identity → when they last spoke: the strip beside the main view shows the latest speakers
    talking: [],           // who is speaking now (others): each is brought onto the gallery page being looked at
    main: null,            // the tile in the main view, sized to its picture on a phone (fitMain)
    board: null,           // the Meeting Board (board.js), made at join on ClikMeet; boardUnread: edits by others while it is closed
    boardUnread: 0,
    boardLoading: null,    // the one fetch of its module
    boardInbox: [],        // data-channel frames that arrived before the board existed
    boardWide: false,      // the board taking the stage on a laptop (Expand)
    sheet: null,           // the phone's sheet controller while the Board is open (sheet.js); sheetDetent: its last height this call
    // Live Sense (CR-20260929-004; D194 B): the panel beside the board for everyone in a ClikMeet room, read-only for guests; senseInbox: messages before it existed
    sense: null,
    senseOpen: false,
    senseInbox: [],
    sheetDetent: "half",
    cam: null,             // the site camera on the stage (D555 A), by its identity — the host's choice, sent in his state
    siteUrl: "",           // the host's: the live site link Helix made
    siteSay: "",           // the host's: why there is no link here ("Get the site link from ClikWork")
    last: { micOn: joined.micOn, camOn: joined.camOn }, // the microphone and camera as last set: an automatic rejoin keeps them
  };
  const tiles = new Map();
  const timers = [];

  // ------------------------------------------------------------------------------------------ the frame --
  const timerEl = h("span", { text: "00:00" });
  const myQuality = h("span", { class: "quality q3", title: "Your connection" }, h("i"), h("i"), h("i"));
  const layoutBtn = h("button", { class: "chip-btn hide-phone", type: "button", onclick: () => toggleLayout() });
  const fullBtn = h("button", { class: "chip-btn hide-phone", type: "button", onclick: () => toggleFull() });
  // ClikMeet M4 (D135 A): everyone sees while the call is transcribed — read from the room itself, so the host's switch
  // shows on every screen at once. Beside the clock, never in the title, and only in a ClikMeet room: Canvas's call is
  // untouched.
  const scribeChip = info.inRoom ? h("span", { class: "scribe-chip", role: "status", hidden: true }, "Transcribing") : null;
  // D137 A: a red "Recording" beside it while the host records — the same room metadata, so every screen shows it at once.
  const recChip = info.inRoom ? h("span", { class: "rec-chip", role: "status", hidden: true }, "Recording") : null;
  const topbar = h("header", { class: "topbar" },
    h("div", { class: "title" }, isCanvas ? h("span", { class: "brand canvas-brand" }, h("b", { text: "Walkin" })) : h("span", { class: "brand clikmeet-brand" }, h("b", { text: "Talkin" })), h("b", { text: info.meeting.project || info.meeting.title })),
    h("div", { class: "mid" }, timerEl, myQuality, recChip, scribeChip),
    h("div", { class: "right" }, layoutBtn, fullBtn));

  const mainArea = h("div", { class: "main-area" });
  const gallery = h("div", { class: "gallery" });
  const filmstrip = h("div", { class: "filmstrip", "aria-label": "Everyone else" });
  const waiting = h("div", { class: "waiting", hidden: true });
  // Larger calls: the gallery shows a page of faces at a time; these turn the page (hidden while everyone fits).
  const pageLabel = h("span", { class: "page-label" });
  const prevBtn = h("button", { class: "page-btn", type: "button", "aria-label": "Previous page of people", onclick: () => turnPage(-1) });
  const nextBtn = h("button", { class: "page-btn", type: "button", "aria-label": "Next page of people", onclick: () => turnPage(1) });
  prevBtn.innerHTML = "&#8249;"; nextBtn.innerHTML = "&#8250;";
  const pager = h("div", { class: "pager", hidden: true }, prevBtn, pageLabel, nextBtn);
  const moreTile = h("button", { class: "tile more-tile", type: "button", onclick: () => openPanel("people") }, h("span"));
  const stage = h("section", { class: "stage", "aria-label": "Meeting" }, mainArea, gallery, filmstrip, pager, waiting);
  // Larger calls are ClikMeet's (M5): pages, the strip's cap and "+N". Canvas's call keeps every face on screen, as before.
  const paged = !isCanvas;
  stage.classList.toggle("paged", paged);
  const audioBox = h("div", { hidden: true });

  const micBtn = ctl("Mute", "mic", () => toggleMic());
  const camBtn = ctl("Stop video", "video", () => toggleCam());
  micBtn.classList.add("keep"); camBtn.classList.add("keep"); // the two that stay in the pill when the board takes the whole phone
  const backBtn = ctl("Back to the call", "minimize", () => state.sheet?.set("half"), "Back");
  backBtn.classList.add("back-to-call");
  const micCaret = caret("Microphone and speaker", (el) => deviceMenu(el, "audio"));
  const camCaret = caret("Camera", (el) => deviceMenu(el, "video"));
  const peopleBtn = ctl("Participants", "users", () => openPanel("people"), "People");
  const peopleBadge = h("span", { class: "badge", text: "1" });
  peopleBtn.append(peopleBadge);
  const chatBtn = ctl("Chat", "chat", () => openPanel("chat"));
  const chatBadge = h("span", { class: "badge", hidden: true });
  chatBtn.append(chatBadge);
  // The Meeting Board (ClikMeet; D187 A): the plan on screen while it is made. A dot when someone else edits it closed.
  const boardBtn = ctl("Board", "board", () => openPanel("board"));
  const boardBadge = h("span", { class: "badge", hidden: true });
  boardBtn.append(boardBadge);
  boardBtn.hidden = isCanvas;
  // Live Sense (CR-20260929-004; D194 B): what the scribe hears, for everyone in a ClikMeet room to see and for the
  // host and colleagues to sign onto the board. A guest's panel is read-only.
  // `info.room` reaches the host and colleagues only; `inRoom` says a ClikMeet room to everyone in it (the link route).
  const senseAllowed = !isCanvas && Boolean(info.room || info.inRoom);
  const senseSigns = senseAllowed && (isHost || pass.role === "rm");
  const senseBtn = ctl("Live Sense", "sense", () => toggleSense(), "Sense");
  const senseBadge = h("span", { class: "badge", hidden: true });
  senseBtn.append(senseBadge);
  senseBtn.hidden = !senseAllowed;
  senseBtn.classList.add("hide-phone"); // a phone's bar is full (Board sits on it, D191 A): Live Sense is in More there
  // The Board sits on the bar on every phone (D191 A): the bar is slimmer there (studio.css). The More item is kept for
  // any window where the button is not on screen.
  const boardInBar = () => !isCanvas && boardBtn.offsetParent !== null;
  const canShare = Boolean(navigator.mediaDevices?.getDisplayMedia) && !phone();
  const shareBtn = ctl("Share", "share", () => toggleShare());
  shareBtn.hidden = !canShare;
  const reactBtn = ctl("Reactions", "smile", () => reactionMenu());
  reactBtn.classList.add("hide-phone"); // on a phone the bar has room for five; reactions move into More, as in Zoom's app
  const moreBtn = ctl("More", "more", () => moreMenu());
  // The site camera (D555 A): the host's box, on an RM-hosted plain call only.
  const siteBtn = ctl("Site camera", "video", () => openPanel("site"), "Site");
  siteBtn.hidden = !extras.siteBox;
  const leaveBtn = h("button", { class: "leave", type: "button", text: isHost ? "End" : "Leave", "aria-haspopup": "menu", onclick: () => leaveMenu() });
  const controls = h("footer", { class: "controls", "aria-label": "Meeting controls" },
    h("div", { class: "group" }, h("div", { class: "split" }, micBtn, micCaret), h("div", { class: "split" }, camBtn, camCaret)),
    h("div", { class: "group center" }, peopleBtn, chatBtn, boardBtn, senseBtn, siteBtn, shareBtn, reactBtn, moreBtn),
    h("div", { class: "group" }, backBtn, leaveBtn));

  // display: contents (studio.css) — the panel itself is the grid cell, so it is held to the stage's row and scrolls
  // inside; as a plain block it grew with every message past the screen (audit U1, 26 Sep).
  const panelSlot = h("div", { class: "panel-slot" });
  // ClikMeet's door (M3; D127 A): guests who knocked on the room link, for the host to admit or refuse.
  // One small notice with the count (CR-20260926-001 U3): the names, Admit and Admit all are in Participants, so five
  // people at the door never cover the stage.
  const doorNote = h("button", { class: "door-note", type: "button", "aria-live": "polite", hidden: true, onclick: () => { if (state.panel !== "people") openPanel("people"); } });
  const senseSlot = h("div", { class: "sense-slot" }); // Live Sense's own cell: beside the board on a laptop (studio.css .has-sense)
  const shell = h("div", { class: "call" }, topbar, stage, doorNote, panelSlot, senseSlot, controls, audioBox);
  // Live assist on the plain call (L3.3, D555 A): the host's floating panel, as in the navigator. No canvas, so the
  // assist names no scene to show: a nudge has nowhere to go and is never acted on here.
  const assist = extras.assistPanel ? assistPanel({
    names: () => ({ customer: firstName([...room.remoteParticipants.values()].find((p) => roleOf(p) === "customer")?.name) }),
    onShow: () => {},
    isShowing: () => false,
  }) : null;
  if (assist) shell.append(assist.el);
  document.body.classList.add("ms-dark");
  root.replaceChildren(shell);

  // `short`: the word under the icon where the bar is narrow (the accessible name stays the full label)
  function ctl(label, iconName, onClick, short = label) {
    const b = h("button", { class: "ctl", type: "button", "aria-label": label, onclick: onClick });
    b.innerHTML = icon(iconName);
    // the word under the icon: the full one, and a shorter one where the bar is narrow (studio.css picks)
    b.append(short === label ? h("span", { class: "lbl", text: label }) : h("span", { class: "lbl" }, h("span", { class: "lbl-long", text: label }), h("span", { class: "lbl-short", text: short })));
    return b;
  }
  function setCtl(b, label, iconName, cls) {
    b.firstElementChild.outerHTML = icon(iconName);
    b.querySelector(".lbl").textContent = label;
    b.setAttribute("aria-label", label);
    b.classList.remove("is-off", "is-on");
    if (cls) b.classList.add(cls);
  }
  function caret(label, onClick) {
    const b = h("button", { class: "caret", type: "button", "aria-label": label, "aria-haspopup": "menu" });
    b.innerHTML = icon("caret");
    b.addEventListener("click", () => onClick(b));
    return b;
  }

  // ------------------------------------------------------------------------------------------- tiles --
  function keyOf(p, source) { return `${p.identity}|${source}`; }

  function ensureTile(p, source) {
    const key = keyOf(p, source);
    let t = tiles.get(key);
    if (t) return t;
    const isScreen = source === Track.Source.ScreenShare;
    const video = h("video", { autoplay: true, playsinline: true, muted: true });
    const nameEl = h("span");
    const roleEl = h("em");
    const micEl = h("span", { class: "mic-off", hidden: true });
    micEl.innerHTML = icon("micOff");
    const label = h("div", { class: "label" }, micEl, nameEl, roleEl);
    const hand = h("div", { class: "hand", text: "✋ Hand raised", hidden: true });
    const q = h("span", { class: "q quality q3", hidden: true }, h("i"), h("i"), h("i"));
    const pin = h("button", { class: "pin", type: "button", "aria-label": "Pin to the main view" });
    pin.innerHTML = icon("pin");
    const avatar = h("div", { class: "avatar-wrap" }, h("div", { class: "avatar", text: initials(p.name || p.identity) }));
    const el = h("div", { class: "tile" + (p.isLocal ? " is-self" : "") + (isScreen ? " is-screen" : ""), dataset: { key } },
      video, avatar, isScreen ? h("div", { class: "presenting", text: `${p.isLocal ? "You are" : firstName(p.name) + " is"} presenting` }) : null,
      hand, q, label, pin);
    pin.addEventListener("click", () => { state.pinned = state.pinned === key ? null : key; layout(); });
    t = { key, p, identity: p.identity, source, el, video, nameEl, roleEl, micEl, hand, q, pin, attached: null };
    // The picture's shape, as it arrives and whenever it changes (a phone turned on its side): a portrait picture in
    // a landscape cell is contained, never cropped (fit.js), and the main view on a phone is re-fitted to it.
    const sized = () => { el.classList.toggle("is-portrait", isPortrait(video)); if (state.main === t) fitMain(); };
    video.addEventListener("loadedmetadata", sized);
    video.addEventListener("resize", sized);
    // On a phone a tap on a face in the strip pins it to the main view (Ravi, 28 Sep); the pin button is a hover thing.
    el.addEventListener("click", (e) => {
      if (!phone() || e.target.closest("button") || el.parentElement !== filmstrip || isScreen) return;
      state.pinned = key;
      layout();
    });
    tiles.set(key, t);
    return t;
  }

  // On a phone the main view takes the shape of the picture in it — the whole frame, as large as the stage allows,
  // centred — instead of a cell the picture is cropped to fill (Ravi, 28 Sep; Zoom, Meet and Teams do the same). A
  // laptop's main view keeps the cell and contains the picture (studio.css). A shared screen was contained already.
  function fitMain() {
    const t = state.main;
    if (!t) return;
    if (!phone() || !t.el.classList.contains("has-video") || shell.classList.contains("board-full")) { t.el.style.cssText = ""; return; } // at full the faces are a strip (studio.css)
    const box = fitInside({ width: mainArea.clientWidth, height: mainArea.clientHeight, videoWidth: t.video.videoWidth, videoHeight: t.video.videoHeight });
    if (!box.width || !box.height) { t.el.style.cssText = ""; return; }
    Object.assign(t.el.style, { width: `${box.width}px`, height: `${box.height}px`, justifySelf: "center", alignSelf: "center" });
  }

  function paintTile(t) {
    const { p, source } = t;
    const pub = p.getTrackPublication(source);
    // Only a face on screen has its video: an off-page or capped tile holds none, and adaptive stream then stops that
    // video reaching this browser at all (larger calls — the cap on videos each person receives).
    const track = pub && !pub.isMuted && t.el.isConnected ? pub.track : null;
    if (track !== t.attached) {
      if (t.attached) t.attached.detach(t.video);
      if (track) track.attach(t.video);
      t.attached = track;
    }
    t.el.classList.toggle("has-video", Boolean(track));
    t.el.classList.toggle("is-pinned", state.pinned === t.key);
    if (source === Track.Source.ScreenShare) return;
    t.nameEl.textContent = p.isLocal ? `${p.name || "You"} (you)` : p.name || "Guest";
    const role = roleOf(p);
    const siteTile = extras.siteStage && isSite(p.identity);
    t.roleEl.textContent = role === "host" ? words.hostTile : siteTile ? "site camera" : "";
    t.roleEl.hidden = role !== "host" && !siteTile;
    const mic = p.getTrackPublication(Track.Source.Microphone);
    t.micEl.hidden = Boolean(mic && !mic.isMuted);
    t.hand.hidden = p.attributes?.hand !== "1";
    t.el.classList.toggle("is-speaking", p.isSpeaking && Boolean(mic && !mic.isMuted));
    const cq = p.connectionQuality;
    t.q.hidden = !(cq === ConnectionQuality.Poor || cq === ConnectionQuality.Lost);
    t.q.className = "q quality " + qualityClass(cq);
  }

  function qualityClass(cq) {
    return cq === ConnectionQuality.Excellent ? "q3" : cq === ConnectionQuality.Good ? "q2" : cq === ConnectionQuality.Poor ? "q1" : cq === ConnectionQuality.Lost ? "q0" : "q3";
  }

  function people() {
    return [room.localParticipant, ...room.remoteParticipants.values()];
  }

  function sharing(p) {
    const pub = p.getTrackPublication(Track.Source.ScreenShare);
    return Boolean(pub && pub.track && !pub.isMuted);
  }

  // Where each tile goes. A screen being shared always takes the main view; otherwise the pinned tile, then whoever
  // is speaking. Gallery lays everyone out at the largest size that fits. On a phone: the other person large, you
  // in a small corner window.
  function layout() {
    const everyone = people();
    const alive = new Set();
    for (const p of everyone) {
      alive.add(keyOf(p, Track.Source.Camera));
      ensureTile(p, Track.Source.Camera);
      if (sharing(p)) { alive.add(keyOf(p, Track.Source.ScreenShare)); ensureTile(p, Track.Source.ScreenShare); }
    }
    for (const [key, t] of tiles) {
      if (!alive.has(key)) {
        if (t.attached) t.attached.detach(t.video);
        t.el.remove();
        tiles.delete(key);
        if (state.pinned === key) state.pinned = null;
      }
    }

    // D555 A: the site camera the host put on the stage takes the main view at its own shape, as a shared screen does;
    // a site phone not on the stage is off everyone's grid but the host's (call-extras.js). A NULL meeting: as today.
    if (state.cam && !room.remoteParticipants.has(state.cam)) state.cam = null;
    const camTile = extras.siteStage && state.cam ? tiles.get(keyOf({ identity: state.cam }, Track.Source.Camera)) : null;
    for (const t of tiles.values()) if (extras.siteStage && t.source === Track.Source.Camera && isSite(t.identity)) t.el.classList.toggle("is-screen", t === camTile);
    const screens = [...[...tiles.values()].filter((t) => t.source === Track.Source.ScreenShare), ...(camTile ? [camTile] : [])];
    const cams = everyone.filter((p) => siteVisibleTo(extras, p.identity, state.cam) && !(camTile && p.identity === state.cam))
      .map((p) => tiles.get(keyOf(p, Track.Source.Camera)));
    const self = tiles.get(keyOf(room.localParticipant, Track.Source.Camera));
    const others = cams.filter((t) => t !== self);
    const onPhone = phone();
    // Audit F2: a pin puts that person on the stage — in gallery view too (the laptop default), as Zoom and Teams do.
    const pinnedLive = Boolean(state.pinned && tiles.get(state.pinned));
    const mode = screens.length ? "share" : onPhone || pinnedLive ? "speaker" : state.layout;

    for (const t of tiles.values()) { t.el.classList.remove("pip"); t.el.style.cssText = ""; }
    // Rebuilding the strip takes "+N" out of the page, which drops a keyboard user's focus to <body>: put it back.
    const moreHadFocus = document.activeElement === moreTile;
    mainArea.replaceChildren();
    gallery.replaceChildren();
    filmstrip.replaceChildren();
    pager.hidden = true;
    state.main = null;
    stage.dataset.mode = mode; // studio.css: a landscape phone gives a share the whole stage (audit U8)
    mainArea.hidden = mode === "gallery";
    gallery.hidden = mode !== "gallery";

    if (mode === "gallery") {
      const pg = paged ? galleryPage({ order: cams, speakers: state.talking, page: state.page, size: pageSize({ width: stage.clientWidth || window.innerWidth }) })
        : { shown: cams, page: 0, pages: 1 };
      state.page = pg.page;
      gallery.append(...pg.shown.map((t) => t.el));
      fitGallery(pg.shown);
      pager.hidden = pg.pages < 2;
      pageLabel.textContent = `${pg.page + 1} / ${pg.pages}`;
      prevBtn.disabled = pg.page === 0;
      nextBtn.disabled = pg.page >= pg.pages - 1;
    } else {
      const pinned = state.pinned && tiles.get(state.pinned);
      const speakerTile = state.speaker && tiles.get(keyOf({ identity: state.speaker }, Track.Source.Camera));
      const main = screens[screens.length - 1] || pinned || (speakerTile !== self && speakerTile) || others[0] || self;
      mainArea.append(main.el);
      if (state.main !== main) resetZoom(main);
      state.main = main;
      const pip = onPhone && self && main !== self && mode !== "share";
      if (pip) {
        self.el.classList.add("pip");
        if (state.pipAt) Object.assign(self.el.style, { left: state.pipAt.left, top: state.pipAt.top, right: "auto", bottom: "auto" });
        stage.append(self.el);
      }
      // The strip: every other shared screen, then at most FILMSTRIP_MAX faces — the latest speakers — and one "+N"
      // for the rest (larger calls: a 12-person call on a phone decodes a handful of videos, not eleven).
      const strip = stripOf({ list: cams.filter((t) => t !== main && !(pip && t === self)), lastSpoke: state.lastSpoke,
        max: !paged ? Infinity : onPhone ? FILMSTRIP_MAX.phone : FILMSTRIP_MAX.desk });
      filmstrip.append(...screens.filter((t) => t !== main).map((t) => t.el), ...strip.shown.map((t) => t.el));
      if (strip.more) {
        // One button, kept across layouts (focus is restored below); its accessible name starts with what it shows.
        moreTile.firstElementChild.textContent = `+${strip.more}`;
        moreTile.setAttribute("aria-label", `+${strip.more}: ${strip.more} more people — open Participants to pin one`);
        filmstrip.append(moreTile);
        if (moreHadFocus) moreTile.focus();
      }
    }
    for (const t of tiles.values()) paintTile(t);
    fitMain();
    if (paged) wantCameras();
    if (extras.siteStage && !extras.siteBox) wantSiteCamera();

    const alone = room.remoteParticipants.size === 0;
    waiting.hidden = !alone;
    if (alone) {
      waiting.replaceChildren(h("div", { class: "card" },
        h("h2", {}, h("span", { class: "pulse" }), isHost ? "The room is open" : `${firstName(info.meeting.hostName)} will join in a moment`),
        h("p", { text: isHost ? words.waitingHost : "Stay on this screen — you will see and hear everyone as they arrive." }),
        // A ClikMeet room's one link, right where an empty room's host looks for it (audit U7) — Meet Now has sent nothing.
        keepsDoor ? h("button", { class: "btn waiting-invite", type: "button", onclick: () => copyInvite() }, "Copy invite link") : null));
    }
    peopleBadge.textContent = String(everyone.length);
    layoutBtn.innerHTML = icon(viewing() === "gallery" ? "speaker" : "grid");
    layoutBtn.append(document.createTextNode(viewing() === "gallery" ? "Speaker view" : "Gallery view"));
    if (state.panel === "people") paintPeople();
  }

  // The cap itself (ClikMeet): a camera whose face is not on screen is unsubscribed, so its video is never sent to this
  // browser. Adaptive stream alone did not do it — a video never shown was still received and decoded (10 of 11 on a
  // phone, measured in large.spec.mjs). Coming back on screen subscribes it again; the tile shows the initials until
  // the picture arrives. Voices and shared screens are never touched.
  function wantCameras() {
    for (const p of room.remoteParticipants.values()) {
      const pub = p.getTrackPublication(Track.Source.Camera);
      if (!pub) continue;
      const shown = Boolean(tiles.get(keyOf(p, Track.Source.Camera))?.el.isConnected);
      if (pub.isDesired !== shown) pub.setSubscribed(shown);
    }
  }

  // Her phone and a guest's (D555 A): a site phone's camera is received only while the host has it on the stage — on 4G
  // at the site it is the heaviest picture in the room, and nobody but the host is to see it otherwise.
  function wantSiteCamera() {
    for (const p of room.remoteParticipants.values()) {
      if (!isSite(p.identity)) continue;
      const pub = p.getTrackPublication(Track.Source.Camera);
      const shown = state.cam === p.identity;
      if (pub && pub.isDesired !== shown) pub.setSubscribed(shown);
    }
  }

  function turnPage(by) {
    state.page = Math.max(0, state.page + by);
    layout();
  }

  function fitGallery(list) {
    const n = Math.max(1, list.length);
    const W = gallery.clientWidth || stage.clientWidth, H = gallery.clientHeight || stage.clientHeight - 20, gap = 10;
    let best = { w: 0, cols: 1 };
    for (let cols = 1; cols <= n; cols++) {
      const rows = Math.ceil(n / cols);
      const w = Math.min((W - gap * (cols - 1)) / cols, ((H - gap * (rows - 1)) / rows) * (16 / 9));
      if (w > best.w) best = { w, cols };
    }
    gallery.style.gridTemplateColumns = `repeat(${best.cols}, ${Math.floor(best.w)}px)`;
    gallery.style.justifyContent = "center";
    for (const t of list) t.el.style.height = `${Math.floor(best.w * 9 / 16)}px`;
  }

  // Pinch to zoom on the main view (a phone; Ravi, 28 Sep), as Zoom's app does: two fingers scale the picture up to
  // 3×, one finger pans it while zoomed, a double tap puts it back. The picture is scaled, not the tile: the name
  // stays put. Every change of the main view puts it back.
  const zoom = { s: 1, x: 0, y: 0, pts: new Map(), start: null, lastTap: 0, down: null };
  function applyZoom() {
    const t = state.main;
    if (!t) return;
    const flip = t.el.classList.contains("is-self") ? " scaleX(-1)" : "";
    t.video.style.transform = zoom.s > 1 ? `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})${flip}` : "";
  }
  function resetZoom(next = state.main) {
    if (state.main) state.main.video.style.transform = "";
    zoom.s = 1; zoom.x = 0; zoom.y = 0; zoom.start = null; zoom.pts.clear();
    if (next) next.video.style.transform = "";
  }
  mainArea.addEventListener("pointerdown", (e) => {
    if (!phone() || !state.main || e.target.closest("button")) return;
    zoom.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (zoom.pts.size === 2) {
      const [a, b] = [...zoom.pts.values()];
      zoom.start = { d: Math.hypot(a.x - b.x, a.y - b.y), s: zoom.s };
    } else if (zoom.pts.size === 1) {
      zoom.down = { x: e.clientX, y: e.clientY, at: Date.now() };
      zoom.start = { x: e.clientX - zoom.x, y: e.clientY - zoom.y };
    }
  });
  mainArea.addEventListener("pointermove", (e) => {
    if (!zoom.pts.has(e.pointerId) || !state.main) return;
    zoom.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (zoom.pts.size === 2 && zoom.start?.d) {
      const [a, b] = [...zoom.pts.values()];
      zoom.s = Math.min(3, Math.max(1, zoom.start.s * Math.hypot(a.x - b.x, a.y - b.y) / zoom.start.d));
      if (zoom.s === 1) { zoom.x = 0; zoom.y = 0; }
      applyZoom();
    } else if (zoom.pts.size === 1 && zoom.s > 1 && zoom.start && "x" in zoom.start) {
      const r = state.main.el.getBoundingClientRect(), lim = (zoom.s - 1) / 2;
      zoom.x = Math.max(-r.width * lim, Math.min(r.width * lim, e.clientX - zoom.start.x));
      zoom.y = Math.max(-r.height * lim, Math.min(r.height * lim, e.clientY - zoom.start.y));
      applyZoom();
    }
  });
  // A tap is decided when the finger lifts (one finger, hardly moved, quick) — a second finger's landing or a pan is
  // never one (re-audit: the second pinch in a row read as a double tap). Two taps within 300 ms put the zoom back.
  const fingerUp = (e) => {
    const was = zoom.pts.size;
    zoom.pts.delete(e.pointerId);
    const d = zoom.down;
    zoom.down = null;
    if (e.type === "pointerup" && was === 1 && d && Date.now() - d.at < 300 && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 10) {
      if (Date.now() - zoom.lastTap < 300) { zoom.lastTap = 0; resetZoom(state.main); } else zoom.lastTap = Date.now();
    }
    if (zoom.pts.size < 2) zoom.start = zoom.pts.size ? { x: [...zoom.pts.values()][0].x - zoom.x, y: [...zoom.pts.values()][0].y - zoom.y } : null;
  };
  mainArea.addEventListener("pointerup", fingerUp);
  mainArea.addEventListener("pointercancel", fingerUp);

  // ------------------------------------------------------------------------------------------ controls --
  async function toggleMic() {
    state.selfMute = true; // our own press: the TrackMuted it causes is not the host's
    try { await room.localParticipant.setMicrophoneEnabled(!room.localParticipant.isMicrophoneEnabled); }
    catch { toast("Your microphone is blocked — allow it in the browser's site settings."); }
    finally { state.selfMute = false; }
    paintControls();
  }
  async function toggleCam() {
    const on = !room.localParticipant.isCameraEnabled;
    // ClikMeet: a new camera gets its background before it is published, so the room behind you is never sent (N1).
    // A camera already published keeps its processor through mute and unmute.
    const fresh = on && !room.localParticipant.getTrackPublication(Track.Source.Camera);
    const processor = !isCanvas && fresh ? await rememberedProcessor() : null;
    // A background is chosen and this browser can show one, but it did not load: the camera stays off rather than
    // showing the room behind you (fails closed, as the lobby does).
    if (!isCanvas && fresh && !processor && backgroundsHere() && savedBackground().id !== "none") {
      toast("Your background could not load, so your camera is still off. Try again, or choose None under Camera › Background.");
      return;
    }
    try { await room.localParticipant.setCameraEnabled(on, { resolution: VideoPresets.h720.resolution, ...(processor ? { processor } : {}) }); }
    catch { toast("Your camera is blocked — allow it in the browser's site settings."); }
    paintControls();
    layout();
  }

  // Canvas: one window or tab, never the whole screen — a whole screen shows notifications, other tabs and anything
  // private on the desk, in front of a customer (Ravi, 19 Sep). ClikMeet: the whole screen too, with its sound, so a
  // video played on the screen is heard by everyone (Ravi, 25 Sep).
  async function toggleShare() {
    if (state.shareTrack) return stopShare();
    if (state.cam) { toast("Go back from the site camera first — one live picture at a time."); return; }
    let picked;
    try { picked = await pickShare(isCanvas ? SHARE_TAB_OR_WINDOW : SHARE_ANY, { audio: !isCanvas }); } catch (err) { if (err.refused) toast(err.message, 6000); return; }
    const mst = picked.video;
    if ("contentHint" in mst) mst.contentHint = "detail";
    const track = new LocalVideoTrack(mst, undefined, true);
    // screenShareEncoding: the option LiveKit reads for a share (re-audit 28 Sep: `videoEncoding` here was ignored)
    await room.localParticipant.publishTrack(track, {
      source: Track.Source.ScreenShare, simulcast: false, screenShareEncoding: ScreenSharePresets.h1080fps15.encoding,
    });
    state.shareTrack = track;
    if (picked.audio) {
      // Music and film, not a voice: no silence detection (it clips quiet passages), and played to everyone else by the
      // room's audio box, as any sound in the room is.
      const sound = new LocalAudioTrack(picked.audio, undefined, true);
      try {
        await room.localParticipant.publishTrack(sound, { source: Track.Source.ScreenShareAudio, dtx: false });
        state.shareAudio = sound;
      } catch { sound.stop(); toast("Your screen is shared, but its sound could not be — try sharing again.", 6000); }
    }
    mst.addEventListener("ended", () => stopShare());
    paintControls();
    layout();
  }
  async function stopShare() {
    const t = state.shareTrack;
    if (!t) return;
    state.shareTrack = null;
    const sound = state.shareAudio;
    state.shareAudio = null;
    for (const x of [t, sound].filter(Boolean)) {
      try { await room.localParticipant.unpublishTrack(x); } catch { /* already gone */ }
      x.stop();
    }
    paintControls();
    layout();
  }

  function paintControls() {
    const lp = room.localParticipant;
    const micOn = lp.isMicrophoneEnabled, camOn = lp.isCameraEnabled;
    // as the person has them now, for an automatic rejoin — not the empty room a disconnect leaves behind, and the
    // devices switched to mid-call, not the join page's (re-audit 28 Sep)
    const real = (id) => (id && id !== "default" ? id : ""); // LiveKit's placeholder before a device was chosen is not an id
    if (room.state === "connected") state.last = { micOn, camOn, audio: real(room.getActiveDevice("audioinput")), video: real(room.getActiveDevice("videoinput")) };
    setCtl(micBtn, micOn ? "Mute" : "Unmute", micOn ? "mic" : "micOff", micOn ? "" : "is-off");
    setCtl(camBtn, camOn ? "Stop video" : "Start video", camOn ? "video" : "videoOff", camOn ? "" : "is-off");
    setCtl(shareBtn, state.shareTrack ? "Stop share" : "Share", "share", state.shareTrack ? "is-on" : "");
    fullBtn.innerHTML = icon(document.fullscreenElement ? "minimize" : "maximize");
    fullBtn.append(document.createTextNode(document.fullscreenElement ? "Exit full screen" : "Full screen"));
    const q = lp.connectionQuality;
    myQuality.className = "quality " + qualityClass(q);
    myQuality.title = `Your connection: ${q === ConnectionQuality.Unknown ? "checking" : String(q)}`;
  }

  async function deviceMenu(anchor, which) {
    const content = h("div");
    const kinds = which === "audio" ? [["audioinput", "Microphone"], ["audiooutput", "Speaker"]] : [["videoinput", "Camera"]];
    for (const [kind, label] of kinds) {
      if (kind === "audiooutput" && !("setSinkId" in HTMLMediaElement.prototype)) continue;
      let list = [];
      try { list = await Room.getLocalDevices(kind, false); } catch { /* none */ }
      if (!list.length) continue;
      const active = room.getActiveDevice(kind);
      content.append(h("h4", { text: label }));
      list.forEach((d, i) => content.append(menuItem(d.label || `${label} ${i + 1}`,
        () => room.switchActiveDevice(kind, d.deviceId).catch(() => toast("That device could not be used.")),
        { checked: d.deviceId === active || (!active && i === 0) })));
    }
    content.append(h("hr"));
    // ClikMeet: blur or a picture behind you, changed mid-call (Canvas has none).
    if (which === "video" && !isCanvas && backgroundsHere()) content.append(menuItem("Background and lighting", () => pickBackground(cameraTrack), { iconName: "background" }));
    content.append(menuItem(which === "audio" ? "Audio settings" : "Video settings", () => settings(), { iconName: "settings" }));
    showMenu(anchor, content, { label: which === "audio" ? "Audio" : "Video" });
  }

  function cameraTrack() { return room.localParticipant.getTrackPublication(Track.Source.Camera)?.track || null; }

  async function settings() {
    const body = await deviceForm({
      audioinput: room.getActiveDevice("audioinput"),
      videoinput: room.getActiveDevice("videoinput"),
      audiooutput: room.getActiveDevice("audiooutput"),
    }, (kind, id) => room.switchActiveDevice(kind, id).catch(() => toast("That device could not be used.")));
    await modal("Your devices", body, [{ text: "Done", value: true, class: "btn" }]);
  }

  function reactionMenu() {
    const raised = room.localParticipant.attributes?.hand === "1";
    const content = h("div", {},
      h("div", { class: "reactions" }, REACTIONS.map((e) => h("button", { type: "button", "aria-label": `React ${e}`, text: e, onclick: () => { closeMenu(); react(e); } }))),
      h("hr"),
      menuItem(raised ? "Lower hand" : "Raise hand", () => toggleHand(), { iconName: "hand" }));
    showMenu(reactBtn, content, { label: "Reactions" });
  }

  async function react(emoji) {
    floatOn(room.localParticipant.identity, emoji);
    await send("react", { emoji });
  }
  async function toggleHand() {
    const raised = room.localParticipant.attributes?.hand === "1";
    try { await room.localParticipant.setAttributes({ hand: raised ? "" : "1" }); } catch { toast("Could not raise your hand — try again."); }
    layout();
  }

  function floatOn(identity, emoji) {
    const t = tiles.get(`${identity}|${Track.Source.Camera}`);
    const host = t && t.el.isConnected ? t.el : stage;
    const f = h("div", { class: "float", text: emoji });
    f.style.left = `${20 + Math.random() * 55}%`;
    host.append(f);
    setTimeout(() => f.remove(), 3100);
  }

  function moreMenu() {
    const raised = room.localParticipant.attributes?.hand === "1";
    const content = h("div", {},
      phone() ? h("div", { class: "reactions" }, REACTIONS.map((e) => h("button", { type: "button", "aria-label": `React ${e}`, text: e, onclick: () => { closeMenu(); react(e); } }))) : null,
      phone() ? menuItem(raised ? "Lower hand" : "Raise hand", () => toggleHand(), { iconName: "hand" }) : null,
      phone() ? h("hr") : null,
      !isCanvas && !boardInBar() ? menuItem(state.boardUnread ? "Meeting Board · new" : "Meeting Board", () => openPanel("board"), { iconName: "board" }) : null,
      senseAllowed && senseBtn.offsetParent === null ? menuItem(state.sense?.count() ? `Live Sense · ${state.sense.count()}` : "Live Sense", () => toggleSense(), { iconName: "sense" }) : null,
      phone() ? null : menuItem(viewing() === "gallery" ? "Speaker view" : "Gallery view", () => toggleLayout(), { iconName: viewing() === "gallery" ? "speaker" : "grid" }),
      menuItem(document.fullscreenElement ? "Exit full screen" : "Full screen", () => toggleFull(), { iconName: "maximize" }),
      menuItem("Audio and video settings", () => settings(), { iconName: "settings" }),
      // ClikMeet (M3): the room's one link, for the host to send to anyone else — colleagues sign in, guests knock.
      keepsDoor ? menuItem("Copy invite link", () => copyInvite(), { iconName: "link" }) : null,
      // M4 (D135 A): the host holds a call off the record, or puts it back on.
      isHost && info.inRoom ? menuItem(switches().transcribe ? "Stop transcribing" : "Transcribe this call", () => toggleTranscribe(), { iconName: "chat" }) : null,
      // D137 A: the host records the call; nobody else can.
      // Offered only where recording is switched on for ClikMeet (info.canRecord); "Stop" always, if one is running.
      isHost && info.inRoom && (info.canRecord || switches().recording) ? menuItem(switches().recording ? "Stop recording" : "Record this call", () => toggleRecord(), { iconName: "video" }) : null,
      h("hr"),
      h("h4", { text: "Shortcuts" }),
      h("p", { class: "fine", style: "margin:0 10px 8px", text: isCanvas ? "Alt+A mute · Alt+V video · Alt+S share · Alt+H chat · Alt+U participants · Alt+Y hand" : "Alt+A mute · Alt+V video · Alt+S share · Alt+H chat · Alt+U participants · Alt+B board · Alt+Y hand" }));
    showMenu(moreBtn, content, { label: "More" });
  }

  function switches() {
    try { return { transcribe: false, recording: false, ...JSON.parse(room.metadata || "{}") }; } catch { return { transcribe: false, recording: false }; }
  }
  function paintSwitches() {
    const s = switches();
    if (scribeChip) scribeChip.hidden = !s.transcribe;
    if (recChip) recChip.hidden = !s.recording;
    state.sense?.paintStatus(); // Live Sense listens only while the call is transcribed: its line follows the switch
  }
  async function toggleTranscribe() {
    const on = !switches().transcribe;
    try {
      await moderate(code, "transcribe", { on });
      toast(on ? "This call is being transcribed again" : "Transcription is off — nothing more is written down", 4000, "scribe");
    } catch (err) { toast(err.message || "That did not go through — try again."); }
  }

  async function toggleRecord() {
    const on = !switches().recording;
    try {
      await moderate(code, "record", { on });
      toast(on ? "Recording — everyone in the call can see it" : "Recording stopped. The organiser gets the video in Workin shortly.", 4000, "rec");
    } catch (err) { toast(err.message || "That did not go through — try again."); }
  }

  function copyInvite() {
    const url = `${location.origin}/r/${info.room}`;
    navigator.clipboard.writeText(url).then(() => toast("Invite link copied — colleagues sign in, anyone else asks to join"), () => toast(url, 8000));
  }

  // The door of a ClikMeet room, for whoever keeps it (keepsDoor; door.js): the waiting are listed in Participants and
  // the stage shows only one small notice with the count (CR-20260926-001 U3).
  let doorWatch = null;
  function doorChanged(knocks) {
    state.knocks = knocks;
    doorNote.hidden = !knocks.length;
    doorNote.textContent = knocks.length === 1 ? `${knocks[0].name} is asking to join` : `${knocks.length} people are asking to join`;
    if (state.panel === "people") paintPeople();
  }

  // What the person sees: a pin shows speaker view even in gallery (audit F2), so the button offers what is not on
  // screen — "Gallery view" while pinned takes them back to the gallery (re-audit N4).
  function viewing() { return state.pinned && tiles.get(state.pinned) ? "speaker" : state.layout; }
  function toggleLayout() { const now = viewing(); state.pinned = null; state.layout = now === "gallery" ? "speaker" : "gallery"; layout(); }
  function toggleFull() {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  }

  function leaveMenu() {
    const content = h("div", {},
      // A ClikMeet room's link stands after the call ends (CR-20260926-001 F4); Canvas's meeting is over for good.
      isHost ? menuItem(info.inRoom ? "End for everyone" : "End meeting for everyone", () => endForAll(), { danger: true, iconName: "leave" }) : null,
      menuItem("Leave meeting", () => leave(), { iconName: "leave", danger: !isHost }));
    showMenu(leaveBtn, content, { label: "Leave" });
  }
  async function endForAll() {
    try { await moderate(code, "end"); } catch (err) { toast(err.message); }
  }
  function leave() {
    state.ended = "left";
    room.disconnect(true);
  }

  // --------------------------------------------------------------------------------------- side panels --
  function openPanel(which) {
    state.panel = state.panel === which ? null : which;
    if (state.panel && state.senseOpen && phone()) closeSense(); // a phone has room for one panel
    shell.classList.toggle("has-panel", Boolean(state.panel));
    peopleBtn.classList.toggle("is-on", state.panel === "people");
    chatBtn.classList.toggle("is-on", state.panel === "chat");
    boardBtn.classList.toggle("is-on", state.panel === "board");
    siteBtn.classList.toggle("is-on", state.panel === "site");
    if (state.panel === "chat") { state.unread = 0; chatBadge.hidden = true; }
    if (state.panel === "board") { state.boardUnread = 0; boardBadge.hidden = true; }
    if (state.panel !== "board") { state.board?.close(); state.sheet?.destroy(); state.sheet = null; shell.classList.remove("board-peek", "board-half", "board-full"); }
    shell.classList.toggle("board-open", state.panel === "board"); // a phone's stage makes room above the sheet (studio.css)
    shell.classList.toggle("board-wide", state.boardWide && state.panel === "board"); // the wide layout is the board's alone
    panelSlot.replaceChildren(state.panel === "people" ? peoplePanel() : state.panel === "chat" ? chatPanel() : state.panel === "board" ? boardPanel() : state.panel === "site" && extras.siteBox ? sitePanel() : "");
    requestAnimationFrame(() => layout());
  }

  // The Board's panel: a side panel on a laptop, a bottom sheet on a phone (studio.css .panel.is-board), its module
  // fetched the first time. The document lives on for the call across opening and closing.
  function boardPanel() {
    const body = h("div", { class: "board-body" }, state.board ? state.board.el : h("p", { class: "board-empty", text: "Opening the board…" }));
    const frame = panelFrame("Meeting Board", body);
    frame.classList.add("is-board");
    const header = frame.querySelector("header");
    // A phone's sheet (D191 A, sheet.js): drag the handle or the header between peek, half and full; a tap on the
    // handle goes to the other size; below peek it closes. At full the faces are a strip and the controls a pill
    // (studio.css .board-full). The height is remembered for the call.
    const handle = h("button", { class: "sheet-handle", type: "button", "aria-label": "Board size" });
    frame.prepend(handle);
    if (sheet()) {
      state.sheet?.destroy();
      // At full the sheet stops under the faces strip (STRIP_H, the row studio.css draws under the top bar), never over it.
      state.sheet = attachSheet(frame, { grips: [handle, header], start: state.sheetDetent, top: () => (topbar.offsetHeight || 48) + STRIP_H,
        onDetent: (name) => {
          if (name === "closed") { if (state.panel === "board") openPanel("board"); return; }
          state.sheetDetent = name;
          for (const d of ["peek", "half", "full"]) shell.classList.toggle(`board-${d}`, d === name);
          requestAnimationFrame(() => layout());
        } });
    }
    // Expand (a laptop): the board takes the stage and the faces move to a column (Ravi, 29 Sep: "full screen on desktop")
    if (!phone()) {
      const wide = h("button", { class: "board-wide-btn", type: "button", "aria-label": state.boardWide ? "Shrink the board" : "Expand the board", "aria-pressed": String(state.boardWide),
        onclick: () => { state.boardWide = !state.boardWide; shell.classList.toggle("board-wide", state.boardWide); wide.setAttribute("aria-pressed", String(state.boardWide)); wide.setAttribute("aria-label", state.boardWide ? "Shrink the board" : "Expand the board"); requestAnimationFrame(() => layout()); } });
      wide.innerHTML = icon("maximize");
      frame.querySelector("header").insertBefore(wide, frame.querySelector("header button"));
    }
    // who has the board open: their initials in the header, in their colours (no sentence)
    const seat = (b) => { const close = header.querySelector("button[aria-label^='Close']"); if (close && !header.contains(b.avatars)) { header.insertBefore(b.avatars, close); header.insertBefore(b.here, close); } };
    if (state.board) {
      body.replaceChildren(state.board.el);
      seat(state.board);
      state.board.open();
      return frame;
    }
    ensureBoard().then((b) => {
      const here = panelSlot.querySelector(".panel.is-board .board-body");
      if (state.panel === "board" && here && !here.contains(b.el)) { here.replaceChildren(b.el); seat(b); b.open(); }
    }).catch(() => {
      const here = panelSlot.querySelector(".panel.is-board .board-body");
      here?.replaceChildren(h("p", { class: "board-empty", text: "The board could not load — check your connection and try again." }));
    });
    return frame;
  }
  // The board's one making: at join on ClikMeet (below), or at the first press. One load however many times it is
  // asked for (re-audit: two loads raced); a failure lets the next press try again.
  function ensureBoard() {
    if (state.board) return Promise.resolve(state.board);
    state.boardLoading ||= boardModule().then(({ createBoard }) => {
      // remembered in this tab under the ROOM until B4 keeps it in D1 (a guest who knocks again gets a new link, the same room)
      state.board = createBoard({ room: info.room || code, me: { identity: room.localParticipant.identity, name: room.localParticipant.name || "" },
        people: () => people().map((p) => ({ identity: p.identity, name: p.name || "" })),
        onCount: () => {},
        onRemote: () => { if (state.panel !== "board") { state.boardUnread += 1; boardBadge.textContent = ""; boardBadge.classList.add("is-dot"); boardBadge.hidden = false; } },
        onSwipe: (dir) => state.sheet?.stepBy(dir), // a swipe on the notes moves the phone's sheet one height
        send: (bytes, to) => room.localParticipant.publishData(bytes, { reliable: true, topic: "board", ...(to ? { destinationIdentities: [to] } : {}) }) });
      for (const { payload, from } of state.boardInbox) state.board.receive(payload, from); // what arrived while it loaded
      state.boardInbox = [];
      return state.board;
    }).catch((err) => { state.boardLoading = null; throw err; });
    return state.boardLoading;
  }
  // ------------------------------------------------------------------------------------- Live Sense (CR-20260929-004) --
  // Its own panel, in its own grid cell: beside the board on a laptop (both open at once — the cards are signed onto the
  // board), one at a time on a phone. Made at join for the staff, so the cards arrive from the first moment and the badge
  // counts them while the panel is closed.
  function ensureSense() {
    if (state.sense || !senseAllowed) return state.sense;
    state.sense = createSensePanel({
      me: { identity: room.localParticipant.identity, name: room.localParticipant.name || "" },
      canSign: senseSigns,
      people: () => people().map((p) => ({ identity: p.identity, name: p.name || "" })),
      transcribe: () => switches().transcribe,
      onCount: (n) => { senseBadge.textContent = String(n); senseBadge.classList.remove("is-dot"); senseBadge.hidden = !n; },
      onNew: () => { if (!state.senseOpen) toast(senseSigns ? "Live Sense heard something — open Sense to sign it off" : "Live Sense heard something — open Sense to read it", 3200, "sense"); },
      onEdit: (id, fields) => sendSense({ t: "edit", id, ...fields }),
      onSign: (card, verdict) => senseSign(card, verdict),
    });
    for (const msg of state.senseInbox) state.sense.receive(msg);
    state.senseInbox = [];
    return state.sense;
  }
  // To the scribe and the staff in the call — never the room: a guest's transport has no business with a sign-off.
  // The scribe is hidden (not in remoteParticipants), so it is named from the pass.
  async function sendSense(body) {
    const to = [...people().filter((p) => !p.isLocal && fromStaff(p)).map((p) => p.identity), ...(pass.scribe ? [pass.scribe] : [])];
    if (!to.length) return;
    // `k`: the room's sense key — the scribe believes a sign-off by it when LiveKit hands it no sender (a page it has not
    // registered yet); it travels to the staff and the scribe only, never a guest
    try { await room.localParticipant.publishData(enc.encode(JSON.stringify({ ...body, k: pass.senseKey || "" })), { reliable: true, topic: SENSE_TOPIC, destinationIdentities: to }); }
    catch { toast("That did not send — check your connection."); }
  }
  function toggleSense() {
    if (!senseAllowed) return;
    if (state.senseOpen) { closeSense(); return; }
    if (phone() && state.panel) openPanel(state.panel); // a phone has room for one panel
    state.senseOpen = true;
    shell.classList.add("has-sense");
    senseBtn.classList.add("is-on");
    senseSlot.replaceChildren(sensePanel());
    requestAnimationFrame(() => layout());
  }
  function closeSense() {
    state.senseOpen = false;
    shell.classList.remove("has-sense");
    senseBtn.classList.remove("is-on");
    state.sense?.close();
    senseSlot.replaceChildren();
    requestAnimationFrame(() => layout());
  }
  function sensePanel() {
    const sense = ensureSense();
    const body = h("div", { class: "sense-body" }, sense.el);
    const frame = h("aside", { class: "panel is-sense", "aria-label": "Live Sense" },
      h("header", {}, h("h3", { text: "Live Sense" }), h("button", { type: "button", "aria-label": "Close live sense", onclick: () => closeSense() })), body);
    frame.querySelector("header button").innerHTML = icon("x");
    sense.open();
    return frame;
  }
  // A card signed off: onto the board as a block of its kind (a revision changes the block it revised, if it is still
  // there — its kind too, so a note never carries an owner), or dismissed; the scribe is told either way, with the
  // block's id, so it never proposes the card again. Answers false when the board did not take it (the panel puts
  // the card back).
  // `placed`: card id → the board block this page wrote for it. A sign-off the scribe did not take (a sender it had
  // not registered yet, its own reconnect) brings the card back after 20 s; pressing Add again then UPDATES that block
  // instead of writing a second one (audit D, P2-1).
  const placed = new Map();
  async function senseSign(card, verdict) {
    let block = null;
    if (verdict === "board") {
      try {
        const b = await ensureBoard();
        const { addBlock, findBlock, kindOf, textEdit, textOf, turnInto } = await boardModule();
        const t = toBlock(card);
        const me = { identity: room.localParticipant.identity, name: room.localParticipant.name || "" };
        const known = card.revisesBlock || placed.get(card.id) || null;
        const old = known ? findBlock(b.doc, known) : null;
        // one transaction: one update to the peers, one render, whatever the number of fields
        b.doc.transact(() => {
          if (old) {
            if (kindOf(old.get("type")) !== t.type) turnInto(old, t.type); // a task revised as a note loses its owner; a note as a task may take one
            const yt = old.get("text");
            if (yt && typeof yt !== "string") textEdit(yt, textOf(old), t.text);
            if (t.type === "task") { old.set("owner", t.owner); old.set("due", t.due); }
            block = old.get("id");
          } else {
            const nb = addBlock(b.doc, { type: t.type, text: t.text, by: me });
            if (t.type === "task") { nb.set("owner", t.owner); nb.set("due", t.due); }
            nb.set("via", "sense");
            block = nb.get("id");
          }
        });
        placed.set(card.id, block);
        toast(old ? "The board item is updated" : "Added to the board", 2600, "sense");
        if (state.panel !== "board" && !phone()) openPanel("board");
      } catch (err) {
        toast("The board did not take it — try again."); console.warn("[sense]", err);
        return false;
      }
    }
    await sendSense({ t: "signed", id: card.id, verdict, block, by: { identity: room.localParticipant.identity, name: room.localParticipant.name || "" } });
    return true;
  }
  // The "sense" topic, in: read at the ENGINE's DataPacketReceived, not RoomEvent.DataReceived — the room event hands
  // over a `participant` looked up in remoteParticipants, which is undefined for the hidden scribe AND for a guest it
  // cannot place (one who just left), so it cannot tell them apart; the packet carries the identity the server stamped
  // (`participantIdentity`), which a client cannot set. What is believed is acceptSenseMessage's call (sense.js).
  // livekit-client is pinned to the exact version whose engine does this (package.json; tests/unit/livekit-client.test.mjs).
  const senseViewer = { role: senseSigns ? "staff" : "guest", scribe: pass.scribe || "", key: pass.senseKey || "" };
  const hookedEngines = new WeakSet();
  function hookSense() {
    const engine = room.engine;
    if (!senseAllowed || !engine || hookedEngines.has(engine)) return;
    hookedEngines.add(engine);
    engine.on(EngineEvent.DataPacketReceived, onSensePacket);
  }
  function onSensePacket(packet) {
    if (!senseAllowed || packet?.value?.case !== "user") return;
    const u = packet.value.value;
    if (u?.topic !== SENSE_TOPIC) return;
    let body;
    try { body = JSON.parse(dec.decode(u.payload)); } catch { return; }
    const msg = acceptSenseMessage({ identity: packet.participantIdentity, topic: u.topic, body }, senseViewer);
    if (!msg) { shell.dataset.senseRefused = packet.participantIdentity === "" ? "(blank)" : String(packet.participantIdentity); return; } // the stamp we refused (the e2e reads it)
    const sense = state.sense || ensureSense();
    if (sense) sense.receive(msg);
    else if (state.senseInbox.length < 50) state.senseInbox.push(msg);
  }
  // --------------------------------------------------------------------------------------------- /Live Sense --

  // ------------------------------------------------------------------------- the site camera (D555 A, L3.4) --
  // The host's box on an RM-hosted plain call. Only Helix makes the site link (the RM's ClikWork card): here it is read
  // — the studio answers the live one, or 409 site_via_crm when none exists — never made. When the site phone is in the
  // room he puts its picture on everyone's stage, and takes it off; one live picture at a time.
  let siteBody = null;
  function sitePanel() {
    siteBody = h("div", { class: "people-body site-body" });
    paintSite();
    readSiteLink();
    return panelFrame("Site camera", siteBody);
  }
  async function readSiteLink() {
    try {
      const out = await siteLink(code, false);
      state.siteUrl = typeof out.url === "string" && /^https?:\/\/[^\s/]+\/m\/[A-Za-z0-9-]{6,20}$/.test(out.url) ? out.url : "";
      state.siteSay = "";
    } catch (err) {
      state.siteUrl = "";
      state.siteSay = err.data?.code === "site_via_crm" ? "Get the site link from Workin." : err.message || "The site link could not be read just now.";
    }
    paintSite();
  }
  function showSite(p) {
    if (p) {
      if (state.shareTrack) { toast("Stop sharing your screen first — one live picture at a time."); return; }
      const pub = p.getTrackPublication(Track.Source.Camera);
      if (!pub || !pub.track || pub.isMuted) { toast("The site camera is not sending a picture yet."); return; }
      state.cam = p.identity;
    } else state.cam = null;
    sendCam();
    layout();
    paintSite();
  }
  function paintSite() {
    if (!siteBody || state.panel !== "site") return;
    const on = [...room.remoteParticipants.values()].filter((p) => isSite(p.identity));
    const cust = firstName([...room.remoteParticipants.values()].find((p) => roleOf(p) === "customer")?.name) || "them";
    const rows = [];
    if (on.length) {
      const p = on.find((x) => x.identity === state.cam) || on[0];
      rows.push(h("p", { class: "pen-line", text: `${firstName(p.name) || "Someone"} is on the site camera.` }),
        h("div", { class: "row" }, state.cam === p.identity
          ? h("button", { class: "btn-s ghost", type: "button", text: "Back to the call", onclick: () => showSite(null) })
          : h("button", { class: "btn-s gold", type: "button", text: `Show it to ${cust}`, onclick: () => showSite(p) })),
        h("p", { class: "assist", text: "The builder's person on the site camera can hear everyone." }));
    } else rows.push(h("p", { class: "pen-line", text: "Nobody is on the site camera yet." }));
    if (state.siteUrl) {
      rows.push(h("p", { class: "site-url", text: state.siteUrl.replace(/^https?:\/\//, "") }),
        h("div", { class: "row" }, h("button", { class: "btn-s ghost", type: "button", text: "Copy",
          onclick: () => navigator.clipboard?.writeText(state.siteUrl).then(() => toast("Copied"), () => toast("Copy the link above by hand.")) })),
        h("p", { class: "assist", text: "A new link, or one for someone else at the site: from Workin." }));
    } else if (state.siteSay) rows.push(h("p", { class: "assist", text: state.siteSay }));
    siteBody.replaceChildren(...rows);
  }

  function panelFrame(title, body) {
    const close = h("button", { type: "button", "aria-label": `Close ${title.toLowerCase()}`, onclick: () => openPanel(state.panel) });
    close.innerHTML = icon("x");
    return h("aside", { class: "panel", "aria-label": title }, h("header", {}, h("h3", { text: title }), close), body);
  }

  let peopleList, waitList;
  function peoplePanel() {
    peopleList = h("ul", { class: "plist" });
    waitList = h("section", { class: "waitlist", "aria-label": "Waiting to join" });
    paintPeople();
    return panelFrame("Participants", h("div", { class: "people-body" }, waitList, peopleList));
  }
  function paintWaiting() {
    if (!waitList) return;
    paintWaitlist(waitList, doorWatch ? state.knocks : [], doorWatch || {});
  }
  function paintPeople() {
    if (!peopleList) return;
    paintWaiting();
    peopleList.replaceChildren(...people().map((p) => {
      const mic = p.getTrackPublication(Track.Source.Microphone);
      const micOn = Boolean(mic && !mic.isMuted);
      const camOn = Boolean(p.getTrackPublication(Track.Source.Camera)?.isMuted === false);
      const role = roleOf(p);
      const st = h("span", { class: "state" });
      st.innerHTML = (p.attributes?.hand === "1" ? "✋ " : "") + icon(micOn ? "mic" : "micOff", micOn ? "" : "off") + icon(camOn ? "video" : "videoOff", camOn ? "" : "off");
      const acts = [];
      // ClikMeet's larger calls: anyone past the strip's cap or on another page can be put on the stage from here.
      if (paged && !p.isLocal) {
        const key = keyOf(p, Track.Source.Camera);
        const pinnedNow = state.pinned === key;
        acts.push(h("button", { class: "act", type: "button", text: pinnedNow ? "Unpin" : "Pin",
          "aria-label": `${pinnedNow ? "Unpin" : "Pin"} ${p.name || "Guest"}`,
          onclick: () => { state.pinned = pinnedNow ? null : key; layout(); } }));
      }
      // The host moderates anyone; a co-host guests only (the studio refuses the rest the same way).
      if (!p.isLocal && (isHost || (coHost && (role === "guest" || role === "customer")))) {
        if (micOn) acts.push(h("button", { class: "act", type: "button", text: "Mute", onclick: () => moderate(code, "mute", { identity: p.identity, trackSid: mic.trackSid }).catch((e) => toast(e.message)) }));
        acts.push(h("button", { class: "act", type: "button", text: "Remove", onclick: () => removePerson(p) }));
      }
      return h("li", {},
        h("div", { class: "avatar", text: initials(p.name || p.identity) }),
        h("div", { class: "who" }, h("b", { text: p.isLocal ? `${p.name} (you)` : p.name || "Guest" }), h("small", { text: role === "host" ? words.hostLabel : words.roles[role] || "" })),
        st, ...acts);
    }));
  }
  async function removePerson(p) {
    const ok = await modal(`Remove ${firstName(p.name)}?`, h("p", { text: "They will leave the meeting at once, and their link stops working: they cannot come back to this meeting." }),
      [{ text: "Cancel", value: false }, { text: "Remove", value: true, class: "btn" }]);
    if (ok) moderate(code, "remove", { identity: p.identity }).catch((e) => toast(e.message));
  }

  let chatLog;
  function chatPanel() {
    chatLog = h("div", { class: "chat-log", "aria-live": "polite" });
    paintChat();
    const input = h("textarea", { rows: "1", maxlength: "1000", placeholder: "Message everyone", "aria-label": "Message everyone" });
    const sendBtn = h("button", { type: "submit", "aria-label": "Send" });
    sendBtn.innerHTML = icon("send");
    const form = h("form", { class: "chat-form" }, input, sendBtn);
    input.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); form.requestSubmit(); } });
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const text = input.value.trim();
      if (!text) return;
      input.value = "";
      const msg = { id: crypto.randomUUID(), text, at: Date.now() };
      addChat({ ...msg, from: room.localParticipant.identity, name: room.localParticipant.name, mine: true });
      // A ClikMeet room keeps its chat, so whoever joins later reads it (CR-20260926-001 F7).
      if (info.inRoom) keepChat(code, { ...msg, name: room.localParticipant.name }).catch(() => {});
      await send("chat", msg);
    });
    setTimeout(() => input.focus(), 50);
    return panelFrame("Chat", h("div", { style: "display:contents" }, chatLog, form));
  }
  function addChat(m, { quiet = false } = {}) {
    if (m.id && state.chat.some((c) => c.id === m.id)) return; // the kept copy and the live one are one message
    state.chat.push(m);
    state.chat.sort((x, y) => x.at - y.at);
    if (state.chat.length > 300) state.chat.shift();
    if (state.panel === "chat") paintChat();
    else if (!m.mine && !quiet) {
      state.unread += 1;
      chatBadge.textContent = String(state.unread);
      chatBadge.hidden = false;
      toast(state.unread > 1 ? `${state.unread} new messages — ${firstName(m.name)}: ${m.text.slice(0, 60)}` : `${firstName(m.name)}: ${m.text.slice(0, 80)}`, 4000, "chat");
    }
  }
  function paintChat() {
    if (!chatLog) return;
    if (!state.chat.length) { chatLog.replaceChildren(h("p", { class: "chat-empty", text: "Messages here are seen by everyone in the meeting." })); return; }
    chatLog.replaceChildren(...state.chat.map((m) => h("div", { class: "msg" + (m.mine ? " mine" : "") },
      h("p", { class: "meta", text: `${m.mine ? "You" : m.name} · ${new Date(m.at).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}` }),
      h("div", { class: "bubble", text: m.text }))));
    chatLog.scrollTop = chatLog.scrollHeight;
  }

  // ----------------------------------------------------------------------------------------- messages --
  const enc = new TextEncoder(), dec = new TextDecoder();
  async function send(topic, body) {
    // D555 A: on an RM-hosted call nothing of hers (or anyone's chat) reaches the site phone; an empty list sends nothing.
    // A NULL meeting: undefined, everyone — today's.
    const to = chatTargets(extras, [...room.remoteParticipants.keys()]);
    if (to && !to.length) return;
    try { await room.localParticipant.publishData(enc.encode(JSON.stringify(body)), { reliable: true, topic, ...(to ? { destinationIdentities: to } : {}) }); }
    catch { toast("That did not send — check your connection."); }
  }
  // The host's state on an RM-hosted plain call (D555 A): only `cam`, the site camera on the stage, in the navigator's
  // own topic and shape — so the site phone (site.js) says "Showing to …" exactly as it does for a canvas meeting.
  async function sendCam(to) {
    if (!extras.siteBox) return;
    try {
      await room.localParticipant.publishData(enc.encode(JSON.stringify({ cam: state.cam })),
        { reliable: true, topic: CANVAS_TOPIC.STATE, ...(to ? { destinationIdentities: [to] } : {}) });
    } catch { /* the next change or arrival sends it again */ }
  }
  function onData(payload, participant, _kind, topic) {
    if (topic === SENSE_TOPIC) return; // Live Sense: read at the engine (onSensePacket), where the server's sender stamp is
    if (!participant) return;
    if (topic === "board") {
      // the Meeting Board's frames (binary, board.js): to the board, or kept until it exists
      if (state.board) state.board.receive(payload, participant.identity);
      else if (state.boardInbox.length < 500) state.boardInbox.push({ payload: new Uint8Array(payload), from: participant.identity });
      return;
    }
    let body;
    try { body = JSON.parse(dec.decode(payload)); } catch { return; }
    if (topic === CANVAS_TOPIC.STATE) {
      // D555 A: the host's choice of picture, read only on an RM-hosted call, only from the host, only a site-* in the room
      if (!extras.siteStage || extras.siteBox) return;
      const cam = siteCamFrom(body, participant.identity, [...room.remoteParticipants.keys()]);
      if (cam !== state.cam) { state.cam = cam; if (cam) toast(`${firstName(info.meeting.hostName) || "Your RM"} is showing the site camera`); layout(); }
      return;
    }
    if (topic === "chat" && typeof body.text === "string") {
      addChat({ id: body.id, text: body.text.slice(0, 1000), at: Number(body.at) || Date.now(), from: participant.identity, name: participant.name || "Guest", mine: false });
    } else if (topic === "react" && REACTIONS.includes(body.emoji)) {
      floatOn(participant.identity, body.emoji);
    }
  }

  // ------------------------------------------------------------------------------------------ events --
  const relayout = () => layout();
  room.on(RoomEvent.RoomMetadataChanged, () => paintSwitches());
  room
    .on(RoomEvent.Reconnected, () => hookSense()) // a rebuilt engine after a full reconnect: hooked again (a WeakSet keeps it to once per engine)
    .on(RoomEvent.ParticipantConnected, (p) => {
      toast(`${p.name || "Someone"} joined`, 3200, "presence"); state.board?.joined(p.identity);
      if (extras.guard) guardTracks(room);
      if (extras.siteBox && state.cam) sendCam(p.identity);
      layout(); if (state.panel === "site") paintSite();
    })
    .on(RoomEvent.ParticipantDisconnected, (p) => {
      toast(`${p.name || "Someone"} left`, 3200, "presence"); state.board?.left(p.identity);
      // the site phone dropped while shown: the stage goes back, everywhere (context/22)
      if (extras.siteBox && state.cam === p.identity) { state.cam = null; sendCam(); toast("The site camera dropped — back to the call."); }
      layout(); if (state.panel === "site") paintSite();
    })
    .on(RoomEvent.TrackSubscribed, (track, pub) => {
      if (track.kind === Track.Kind.Audio) audioBox.append(track.attach());
      if (lowVideo && pub?.source === Track.Source.Camera) pub.setVideoQuality(VideoQuality.LOW); // a weak connection: the low layer
      layout();
    })
    .on(RoomEvent.TrackUnsubscribed, (track) => {
      if (track.kind === Track.Kind.Audio) track.detach().forEach((el) => el.remove());
      layout();
    })
    // Audit F1: a mute the host made reaches us as our own TrackMuted — the controls must show it, or "Mute" would
    // unmute. The person is told who muted them.
    .on(RoomEvent.TrackMuted, (pub, p) => {
      if (p?.isLocal) {
        paintControls();
        if (pub?.source === Track.Source.Microphone && !state.selfMute) toast("The host muted you — press Unmute to speak");
      }
      relayout();
    })
    .on(RoomEvent.TrackUnmuted, (pub, p) => { if (p?.isLocal) paintControls(); relayout(); })
    .on(RoomEvent.LocalTrackPublished, (pub) => {
      // A camera turned back on is a new track: it gets the background this device chose (ClikMeet).
      if (!isCanvas && pub?.source === Track.Source.Camera && !pub.track?.getProcessor?.()) restoreBackground(pub.track);
      if (extras.guard) guardTracks(room); // her new track is the site phone's to hear only if it is her microphone
      paintControls(); layout();
    })
    .on(RoomEvent.LocalTrackUnpublished, () => { paintControls(); layout(); })
    .on(RoomEvent.TrackPublished, relayout)
    .on(RoomEvent.TrackUnpublished, relayout)
    .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      const now = Date.now();
      const others = speakers.filter((s) => !s.isLocal);
      for (const s of others) state.lastSpoke.set(s.identity, now);
      state.talking = others.map((s) => s.identity);
      const remote = others[0];
      const moved = Boolean(remote && remote.identity !== state.speaker);
      if (moved) state.speaker = remote.identity;
      // Someone speaking whose face is not on screen (another gallery page, or past the strip's cap) is brought on —
      // with a pin too, where the strip beside the pinned person holds the latest speakers (re-audit).
      const unseen = paged && others.some((s) => !tiles.get(keyOf(s, Track.Source.Camera))?.el.isConnected);
      if ((moved && !state.pinned) || unseen) layout();
      for (const t of tiles.values()) paintTile(t);
    })
    .on(RoomEvent.ParticipantAttributesChanged, (changed, p) => {
      if ("hand" in changed && changed.hand === "1" && !p.isLocal) toast(`${firstName(p.name)} raised a hand`, 3200, "hand");
      layout();
    })
    .on(RoomEvent.ConnectionQualityChanged, (q, p) => { connectionChanged(q, p); paintControls(); for (const t of tiles.values()) paintTile(t); })
    .on(RoomEvent.DataReceived, onData)
    .on(RoomEvent.Reconnecting, () => banner("Your connection dropped — reconnecting you to the meeting… Keep this page open.", "reconnect"))
    // back after a drop: the board asks the room for what it missed and hands over what it wrote meanwhile (B2)
    .on(RoomEvent.Reconnected, () => { banner("", "reconnect"); toast("You are back in the meeting"); state.board?.joined(); layout(); })
    .on(RoomEvent.AudioPlaybackStatusChanged, () => audioGate())
    .on(RoomEvent.MediaDevicesChanged, () => paintControls());

  // One banner at a time: reconnecting outranks weak; when reconnecting clears, a still-weak connection shows again.
  let bannerEl = null;
  const banners = { reconnect: "", weak: "" };
  function banner(text, kind = "reconnect") {
    banners[kind] = text || "";
    const show = banners.reconnect || banners.weak;
    bannerEl?.remove();
    bannerEl = show ? h("div", { class: "banner" + (banners.reconnect ? "" : " weak"), role: "status", text: show }) : null;
    if (bannerEl) document.body.append(bannerEl);
  }
  // Ravi, 28 Sep: a visible "your connection is weak", and the sound first. LiveKit rates our own connection; while
  // it is Poor or Lost the banner says so — the person knows it is their network, not the app — and after
  // WEAK_AFTER_MS of it the other people's cameras are asked for at their lowest quality, so what bandwidth there is
  // carries the voices (LiveKit itself pauses a video the network cannot carry at all). Back to Good, both go.
  const WEAK_AFTER_MS = 8000;
  let weakSince = 0, weakTimer = null, lowVideo = false, lowTimer = null;
  function connectionChanged(q, p) {
    if (!p?.isLocal) return;
    const weak = q === ConnectionQuality.Poor || q === ConnectionQuality.Lost;
    if (weak && !weakSince) {
      weakSince = Date.now();
      banner("Your connection is weak — the sound comes first; video may pause until it recovers.", "weak");
      weakTimer = setTimeout(() => setLowVideo(true), WEAK_AFTER_MS);
    } else if (!weak && weakSince) {
      weakSince = 0;
      clearTimeout(weakTimer);
      weakTimer = null;
      banner("", "weak");
      setLowVideo(false);
    }
  }
  function setLowVideo(on) {
    if (on === lowVideo) return;
    lowVideo = on;
    for (const p of room.remoteParticipants.values()) {
      for (const pub of p.videoTrackPublications.values()) {
        if (pub.source === Track.Source.Camera) { try { pub.setVideoQuality(on ? VideoQuality.LOW : VideoQuality.HIGH); } catch { /* not subscribed */ } }
      }
    }
    // and our own camera goes out at its lowest layer only (the uplink is usually the weak half on a phone), so the
    // microphone has the road; back to every layer once the connection is good again (re-audit 28 Sep)
    const ownLayer = () => {
      const mine = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      try { mine?.setPublishingQuality?.(lowVideo ? VideoQuality.LOW : VideoQuality.HIGH); } catch { /* not published */ }
    };
    ownLayer();
    // Dynacast switches our layers back on whenever a subscriber asks for them: while weak, the low layer is asked for
    // again every few seconds (a no-op when nothing changed).
    clearInterval(lowTimer);
    lowTimer = on ? setInterval(ownLayer, 4000) : null;
    if (on) toast("Weak connection: videos are lowered, yours too, so voices stay clear", 5000, "weak");
  }
  let unlockEl = null;
  function audioGate() {
    unlockEl?.remove();
    unlockEl = null;
    if (!room.canPlaybackAudio) {
      unlockEl = h("button", { class: "btn btn-gold audio-unlock", type: "button", text: "Tap to hear the meeting", onclick: () => room.startAudio().then(audioGate) });
      document.body.append(unlockEl);
    }
  }

  function onKey(e) {
    if (!e.altKey || e.metaKey || e.ctrlKey) return;
    const act = { KeyA: toggleMic, KeyV: toggleCam, KeyS: () => canShare && toggleShare(), KeyH: () => openPanel("chat"), KeyU: () => openPanel("people"), KeyY: toggleHand, KeyF: toggleFull, KeyB: () => !isCanvas && openPanel("board"), KeyL: () => toggleSense() }[e.code];
    if (act) { e.preventDefault(); act(); }
  }
  document.addEventListener("keydown", onKey);
  document.addEventListener("fullscreenchange", paintControls);
  // A phone turned on its side (or back) with the Board open crosses SHEET_MQ: the sheet's controller must go where the
  // sheet's CSS goes (verification round 29 Sep: rotating left a 214 px inline height on the static side panel).
  const onResize = () => {
    if (state.panel === "board" && sheet() !== Boolean(state.sheet)) {
      state.sheet?.destroy(); state.sheet = null; shell.classList.remove("board-peek", "board-half", "board-full");
      state.panel = null; openPanel("board"); // rebuilt for the new shape; the notes are the same document
      return;
    }
    layout();
  };
  window.addEventListener("resize", onResize);

  // ----------------------------------------------------------------------------------------- joining --
  const ended = new Promise((resolve) => {
    room.on(RoomEvent.Disconnected, (reason) => {
      // LiveKit says "deleted" when the host's end removes the room, "closed" when it empties on its own.
      const why = state.ended || (reason === DisconnectReason.ROOM_DELETED || reason === DisconnectReason.ROOM_CLOSED ? "ended"
        : reason === DisconnectReason.PARTICIPANT_REMOVED ? "removed"
        : reason === DisconnectReason.DUPLICATE_IDENTITY ? "elsewhere"
        : reason === DisconnectReason.CLIENT_INITIATED ? "left" : "lost");
      cleanup();
      resolve(why);
    });
  });

  // LiveKit stamps every message with its sender's identity and blanks it only for a hidden sender (the assist): a
  // message naming a sender came from someone in the meeting and is dropped (as the navigator does).
  if (assist) {
    room.registerTextStreamHandler(ASSIST_TOPIC, async (reader, from) => {
      if (!acceptAssist(from)) return;
      try { assist.receive(JSON.parse(await reader.readAll())); } catch { /* a garbled message is dropped */ }
    });
  }
  if (senseAllowed) hookSense(); // before connect: the engine exists from the Room's making, and nothing between connect and the join tail is missed
  await room.connect(pass.url, pass.token);
  // …and again after: LiveKit Cloud's region retry on a FIRST connect rebuilds the engine (recreateEngine) without a
  // Reconnected event, and the first hook would be on an engine that no longer speaks (verification round; WeakSet keeps it to once each)
  if (senseAllowed) hookSense();
  // D555 A / L3.1: before her microphone and camera are published, the site phone is allowed her microphone only.
  if (extras.guard) guardTracks(room);
  const lp = room.localParticipant;
  try {
    if (joined.audioTrack) {
      await lp.publishTrack(joined.audioTrack, { source: Track.Source.Microphone });
      if (!joined.micOn) await lp.setMicrophoneEnabled(false);
    }
    if (joined.videoTrack) {
      if (joined.camOn) await lp.publishTrack(joined.videoTrack, { source: Track.Source.Camera });
      else joined.videoTrack.stop();
    }
  } catch (err) {
    // Connected but could not publish: leave the room before the join page shows the problem, or the person stays in
    // LiveKit as a ghost holding a seat and counting minutes (re-audit N5).
    await room.disconnect().catch(() => {});
    throw err;
  }
  state.startedAt = Date.now();
  timers.push(setInterval(() => { timerEl.textContent = clock((Date.now() - state.startedAt) / 1000); }, 1000));
  // A Huddle's close (D146 A): everyone in the call is told ten minutes before, and again a minute before, that nobody
  // new can join after it. The call itself carries on until its last person leaves, and the words say so.
  const closesAt = Date.parse(info.window?.closesAt || "");
  if (closesAt) {
    const at = formatTime(info.window.closesAt);
    const say = (mins) => toast(`${mins > 1 ? `In ${mins} minutes, at ${at}` : `At ${at}`}, this huddle closes to new people. Everyone here can carry on talking.`, 15000, "closes");
    for (const mins of [10, 1]) {
      const wait = closesAt - mins * 60_000 - Date.now();
      if (wait > 0) timers.push(setTimeout(() => say(mins), wait));
    }
  }
  if (keepsDoor) doorWatch = watchDoor(code, doorChanged);
  paintSwitches();
  // What was said before this person joined (F7): kept messages, quietly — the badge counts only what arrives live.
  if (info.inRoom) {
    readChatLog(code).then(({ messages }) => {
      const me = room.localParticipant.identity;
      for (const m of messages || []) addChat({ ...m, mine: m.from === me }, { quiet: true });
    }).catch(() => {});
  }
  paintControls();
  layout();
  audioGate();
  enableDrag();
  // ClikMeet: the Meeting Board from the first moment, so this person answers a newcomer and sees the dot (B2)
  if (!isCanvas) ensureBoard().catch(() => {});
  if (senseAllowed) ensureSense(); // Live Sense: the cards count on the badge from the first moment

  function enableDrag() {
    // The phone's small self-view can be moved out of the way.
    let drag = null;
    stage.addEventListener("pointerdown", (e) => {
      const pip = e.target.closest(".pip");
      if (!pip || shell.classList.contains("board-full")) return; // in the strip the self tile is a tile, not a draggable pip
      const r = pip.getBoundingClientRect(), s = stage.getBoundingClientRect();
      drag = { pip, dx: e.clientX - r.left, dy: e.clientY - r.top, s };
      pip.setPointerCapture(e.pointerId);
    });
    stage.addEventListener("pointermove", (e) => {
      if (!drag) return;
      const { pip, dx, dy, s } = drag;
      const x = Math.min(Math.max(0, e.clientX - s.left - dx), s.width - pip.offsetWidth);
      const y = Math.min(Math.max(0, e.clientY - s.top - dy), s.height - pip.offsetHeight);
      pip.style.left = `${x}px`; pip.style.top = `${y}px`; pip.style.right = "auto"; pip.style.bottom = "auto";
      state.pipAt = { left: `${x}px`, top: `${y}px` };
    });
    stage.addEventListener("pointerup", () => { drag = null; });
  }

  function cleanup() {
    timers.forEach(clearInterval);
    doorWatch?.stop();
    clearToasts();
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("fullscreenchange", paintControls);
    window.removeEventListener("resize", onResize);
    closeMenu();
    clearTimeout(weakTimer);
    clearInterval(lowTimer);
    state.board?.destroy();
    state.board = null;
    if (assist) { try { room.unregisterTextStreamHandler(ASSIST_TOPIC); } catch { /* gone */ } assist.destroy(); }
    state.sense?.destroy();
    state.sense = null;
    state.sheet?.destroy();
    state.sheet = null;
    banner("", "reconnect");
    banner("", "weak");
    unlockEl?.remove();
    for (const t of tiles.values()) if (t.attached) t.attached.detach(t.video);
    state.shareTrack?.stop();
    for (const pub of room.localParticipant.trackPublications.values()) pub.track?.stop();
    joined.audioTrack?.stop();
    joined.videoTrack?.stop();
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    document.body.classList.remove("ms-dark");
  }

  // `last`: the microphone and camera as the person had them, for an automatic rejoin after a lost connection (main.js).
  return { room, ended, last: () => ({ ...state.last }) };
}
